// Editor.js inline tools for text/heading formatting: text colour and font
// size. Both wrap the selection in a <span> — colour as an inline
// `style="color:…"`, size as one `fs-*` class from the token type scale
// (fs-xs/fs-base/fs-h6…fs-h1/fs-d4…fs-d1 →
// var(--font-size-xs)/var(--h6)…var(--h1)/var(--display-4)…var(--display-1)).
// Both renderers (template sanitizeInline, portal section-preview) re-permit
// exactly those two attribute shapes on spans and strip everything else.
// Range surgery lives in inline-span-dom.js.

import {
  FS_CLASS_RE,
  applyToRange,
  cleanupSpan,
  isFormattingSpan,
} from "./inline-span-dom.js";

/** Token-scale sizes offered by the font-size tool ("" = default/clear).
 * "Heading N" renders at the same size as <hN> without using the heading tag;
 * the display sizes continue the scale past Heading 1 (d4 = one step above …
 * d1 = four). */
export const FONT_SIZES = [
  { key: "", label: "Default" },
  { key: "xs", label: "Small" },
  { key: "base", label: "Regular" },
  { key: "h6", label: "Heading 6" },
  { key: "h5", label: "Heading 5" },
  { key: "h4", label: "Heading 4" },
  { key: "h3", label: "Heading 3" },
  { key: "h2", label: "Heading 2" },
  { key: "h1", label: "Heading 1" },
  { key: "d4", label: "Display 4" },
  { key: "d3", label: "Display 3" },
  { key: "d2", label: "Display 2" },
  { key: "d1", label: "Display 1" },
];

const COLOR_ICON =
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17l3-9 3 9M10.2 14h5.6"/><path d="M4 21h16" stroke-width="3"/></svg>';
const SIZE_ICON =
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l2.5-8 2.5 8M4 14.5h3"/><path d="M12 17l3.5-11 3.5 11M13.2 13.5h4.6"/></svg>';

function stripColor(span) {
  span.style.removeProperty("color");
}

function stripFontSize(span) {
  // Snapshot before removing — classList is live during iteration.
  const stale = Array.from(span.classList).filter((cls) => FS_CLASS_RE.test(cls));
  if (stale.length) span.classList.remove(...stale);
}

/**
 * Base inline tool: a toolbar button that toggles an options panel rendered
 * via renderActions(). Subclasses build the panel (buildPanel) and pick a
 * value; apply() routes the change through applyToRange so it lands on the
 * exact selection, however that overlaps existing formatting spans. The
 * selection is saved before the panel opens and restored before applying, so
 * clicking panel controls keeps the range.
 */
class InlineSpanTool {
  static get isInline() {
    return true;
  }

  /**
   * Keep our spans through Editor.js's own save-time sanitizer. `br: true` is
   * also merged into every inline field's sanitize (Editor.js unions all inline
   * tools' sanitizeConfig into each block's text rules), so Shift+Enter soft
   * breaks survive the save — the Header tool's own sanitize is `text: {}`,
   * which otherwise strips <br> so line breaks vanished in the section view and
   * on the live site while still showing in the editor's contenteditable.
   */
  static get sanitize() {
    return { span: { style: true, class: true }, br: true };
  }

  constructor({ api }) {
    this.api = api;
    this.button = null;
    this.panel = null;
    this.open = false;
  }

  render() {
    this.button = document.createElement("button");
    this.button.type = "button";
    this.button.classList.add(this.api.styles.inlineToolButton);
    this.button.innerHTML = this.icon;
    return this.button;
  }

  renderActions() {
    this.panel = document.createElement("div");
    this.panel.className = "pgs-inline-panel";
    this.panel.hidden = true;
    this.buildPanel(this.panel);
    return this.panel;
  }

  /** Button click: toggle the panel, keeping the selection for later. */
  surround() {
    this.open = !this.open;
    if (this.open) this.api.selection.save();
    if (this.panel) {
      this.panel.hidden = !this.open;
      if (this.open) this.syncPanel();
    }
  }

  checkState() {
    const active = Boolean(this.currentSpan());
    this.button?.classList.toggle(this.api.styles.inlineToolButtonActive, active);
    // Return false, not `active`. In Editor.js 2.31 the inline toolbar is a
    // Popover and this return value feeds the tool's `renderActions` panel
    // `isOpen`: returning true auto-expands the panel whenever the caret sits
    // in one of our spans. That fights our own hidden/open toggle in surround()
    // — once text was coloured, the panel opened by the Popover but stayed
    // `hidden` (surround never ran), so the picker looked dead on reopen. The
    // button highlight above is all we want from checkState.
    return false;
  }

  clear() {
    this.open = false;
    if (this.panel) this.panel.hidden = true;
  }

  /**
   * The nearest OF OUR formatting spans the caret sits in, if any. Climbs past
   * Editor.js's transient fake-background span (see isFormattingSpan) so panel
   * state and the collapsed-caret path never touch the highlight wrapper.
   * Only trustworthy for reads and for a collapsed caret — findParentTag
   * matches on EITHER selection boundary, so for a real range this can name a
   * span the selection merely starts or ends in. apply() never uses it for
   * ranges for exactly that reason.
   */
  currentSpan() {
    let el = this.api.selection.findParentTag("SPAN");
    while (el && el.tagName === "SPAN") {
      if (isFormattingSpan(el)) return el;
      el = el.parentElement;
    }
    return null;
  }

  /**
   * Apply a change to the restored selection. `strip` removes this tool's
   * property from a span; `mutate` writes the new value, or null when
   * clearing. A collapsed caret inside one of our spans restyles that whole
   * span; a real range goes through applyToRange (extract → strip → wrap →
   * normalise), which handles selections that straddle span boundaries.
   */
  apply(strip, mutate) {
    this.api.selection.restore();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (range.collapsed) {
      const span = this.currentSpan();
      if (span) {
        strip(span);
        mutate?.(span);
        cleanupSpan(span);
      }
    } else {
      const wrapper = applyToRange(range, strip, mutate);
      if (wrapper) this.api.selection.expandToTag(wrapper);
    }
    this.api.inlineToolbar.close();
  }

  /** Panel option button that preserves the selection on click. */
  option(label, onPick) {
    const el = document.createElement("button");
    el.type = "button";
    el.className = "pgs-inline-panel__option";
    if (typeof label === "string") el.textContent = label;
    // mousedown would collapse the saved selection's visual state; the
    // fake-background Editor.js applies keeps the range, but don't gamble.
    el.addEventListener("mousedown", (e) => e.preventDefault());
    el.addEventListener("click", onPick);
    return el;
  }

  /** Refresh the panel's selected state from the current span (optional). */
  syncPanel() {}
}

/**
 * Text colour: theme default (clears), the tenant palette, or a custom colour
 * from the native picker. Stored as `style="color:…"` on the span; the
 * renderers only accept colour-shaped values (SAFE_COLOR_RE).
 */
export class ColorInlineTool extends InlineSpanTool {
  static get title() {
    return "Text colour";
  }

  constructor({ api, config }) {
    super({ api });
    this.config = config ?? {};
    this.icon = COLOR_ICON;
  }

  setColor(color) {
    this.apply(
      stripColor,
      color
        ? (span) => {
            span.style.color = color;
          }
        : null
    );
  }

  buildPanel(panel) {
    const row = document.createElement("div");
    row.className = "pgs-inline-panel__row";

    const theme = this.option("", () => this.setColor(""));
    theme.classList.add("pgs-inline-panel__swatch", "pgs-inline-panel__swatch--theme");
    theme.title = "Theme colour";
    row.appendChild(theme);

    for (const color of this.config.getPalette?.() ?? []) {
      const swatch = this.option("", () => this.setColor(color));
      swatch.classList.add("pgs-inline-panel__swatch");
      swatch.style.backgroundColor = color;
      swatch.title = color;
      row.appendChild(swatch);
    }

    const custom = this.option("Custom", () => this.pickCustom());
    row.appendChild(custom);

    panel.appendChild(row);
  }

  /**
   * Open the native colour picker and apply the chosen colour once.
   *
   * The <input> is parented to <body>, not the panel: opening the picker moves
   * focus out of the editor, which closes the inline toolbar and tears this
   * panel (and any input inside it) down mid-pick. We also apply on `change`
   * (fires once, when the picker is dismissed) rather than `input` — `input`
   * fires live while dragging, and each event ran apply() → inlineToolbar.close(),
   * so the popup vanished the instant the picker was touched. The saved range
   * lives on api.selection (a separate SelectionUtils from the toolbar's), so it
   * survives the toolbar closing and restore() still lands the colour.
   */
  pickCustom() {
    document.querySelectorAll("input.pgs-inline-custom-color").forEach((el) => el.remove());
    const input = document.createElement("input");
    input.type = "color";
    input.className = "pgs-inline-custom-color";
    input.style.cssText = "position:fixed;left:-9999px;top:0";
    input.setAttribute("aria-hidden", "true");
    document.body.appendChild(input);
    input.addEventListener(
      "change",
      () => {
        this.setColor(input.value);
        input.remove();
      },
      { once: true }
    );
    input.click();
  }
}

/**
 * Font size: one step from the token type scale, stored as an `fs-*` class on
 * the span ("Default" clears it). Sizes track the tenant's typography-scale
 * tokens, so they follow the site's branding.
 */
export class FontSizeInlineTool extends InlineSpanTool {
  static get title() {
    return "Font size";
  }

  constructor({ api }) {
    super({ api });
    this.icon = SIZE_ICON;
  }

  setSize(key) {
    this.apply(stripFontSize, key ? (span) => span.classList.add(`fs-${key}`) : null);
  }

  buildPanel(panel) {
    this.optionEls = new Map();
    for (const { key, label } of FONT_SIZES) {
      const el = this.option(label, () => this.setSize(key));
      this.optionEls.set(key, el);
      panel.appendChild(el);
    }
  }

  syncPanel() {
    const span = this.currentSpan();
    let current = "";
    for (const cls of span?.classList ?? []) {
      if (FS_CLASS_RE.test(cls)) current = cls.slice(3);
    }
    for (const [key, el] of this.optionEls) {
      el.classList.toggle("pgs-inline-panel__option--active", current === key);
    }
  }
}
